// HTML string generators for the blog editor.
//
// Extracted from BlogsPage.js. Most functions are pure: they take
// element/offer data and return HTML strings. Two dependencies cross
// the module boundary:
//   - getPreset (imported from textStyle) — module-level import.
//   - generateAffiliateLink — defined in BlogsPage (closes over user.id),
//     passed in as an explicit parameter to functions that need it.
//
// NOTE: source indentation is preserved verbatim because some functions
// use template literals whose interior whitespace is part of the emitted
// HTML. Reformatting these by hand would silently change the rendered
// output. Leave indentation alone unless you also re-verify HTML output.

import { getPreset } from '../textStyle';

export const generateOfferElementHTML = (offer, affiliateLink, blogTitle = '') => {
    return `<div style="border: 2px solid #e0e0e0; border-radius: 12px; padding: 20px; margin: 20px 0; font-family: Arial, sans-serif; max-width: 400px; position: relative; background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);">
  <!-- Blog Name -->
  <div style="margin-bottom: 10px;">
    <div style="font-size: 12px; color: #666; text-transform: uppercase; font-weight: bold;">BLOG NAME</div>
    <div style="font-size: 16px; color: #333; font-weight: bold;">${blogTitle || 'How to get Protein in Your Body'}</div>
  </div>
  
  <!-- Offer Name -->
  <div style="margin-bottom: 10px;">
    <div style="font-size: 12px; color: #666; text-transform: uppercase; font-weight: bold;">OFFER NAME</div>
    <div style="font-size: 16px; color: #333; font-weight: bold;">${offer.title}</div>
  </div>
  
  <!-- Affiliate Link -->
  <div style="margin-bottom: 15px;">
    <div style="font-size: 12px; color: #666; text-transform: uppercase; font-weight: bold;">AFFILIATE LINK</div>
    <div style="font-size: 12px; color: #1976d2; background: #f0f0f0; padding: 5px; border-radius: 4px; font-family: monospace;">${affiliateLink}</div>
  </div>
  
  <!-- Purchase Button -->
  <button style="background: #9c27b0; color: white; border: none; padding: 12px 24px; border-radius: 6px; font-weight: bold; width: 100%; margin-bottom: 15px; cursor: pointer;">PURCHASE NOW!!</button>
  
  <!-- Product Display -->
  <div style="display: flex; align-items: center; gap: 15px; margin-bottom: 15px; position: relative;">
    <!-- Product Image -->
    <div style="width: 80px; height: 80px; background: #333; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: white; font-size: 12px; text-align: center; font-weight: bold;">
      MTN OPS<br/>MAGNUM
    </div>
    
    <!-- Product Info -->
    <div style="flex: 1;">
      <div style="font-size: 14px; font-weight: bold; margin-bottom: 5px;">${offer.title}</div>
      <div style="font-size: 12px; color: #666; margin-bottom: 8px;">${offer.description}</div>
      
      <!-- Nutritional Info -->
      <div style="display: flex; gap: 5px; flex-wrap: wrap;">
        <span style="background: #e0e0e0; padding: 2px 6px; border-radius: 12px; font-size: 10px;">5.5g BCAAs</span>
        <span style="background: #e0e0e0; padding: 2px 6px; border-radius: 12px; font-size: 10px;">24g Protein</span>
        <span style="background: #e0e0e0; padding: 2px 6px; border-radius: 12px; font-size: 10px;">Pure Nutrients</span>
      </div>
    </div>
    
    <!-- Discount Badge -->
    ${offer.discountPercentage ? `<div style="position: absolute; top: -10px; right: -10px; background: #9c27b0; color: white; border-radius: 50%; width: 50px; height: 50px; display: flex; align-items: center; justify-content: center; text-align: center; font-size: 10px; font-weight: bold;">${offer.discountPercentage}%<br/>OFF</div>` : ''}
  </div>
  
  <!-- Final Purchase Button -->
  <button style="background: #9c27b0; color: white; border: none; padding: 10px 20px; border-radius: 6px; font-weight: bold; width: 100%; cursor: pointer;">BUY</button>
</div>`;
  };

  // HTML generation functions from OffersPage for saved offer elements
export const generateAffiliateElementHTML = (element, offer, generateAffiliateLink) => {
    if (!element || !element.content) {
      return '';
    }

    // Check if offer is valid before proceeding
    if (!offer || !offer._id) {
      console.warn('Invalid offer provided to generateAffiliateElementHTML:', offer);
      return element.content || '';
    }

    let htmlContent = '';

    try {
      // Parse the content if it's a JSON string
      let parsedContent = element.content;
      if (typeof element.content === 'string') {
        parsedContent = JSON.parse(element.content);
      }

      // Debug: Check for video elements with aspectRatio
      if (parsedContent.elements && Array.isArray(parsedContent.elements)) {
        parsedContent.elements.forEach((el, idx) => {
          if (el.type === 'video') {
            console.log(`🎥 [GENERATE] Video element ${idx} from saved element:`, {
              aspectRatio: el.styles?.aspectRatio,
              height: el.styles?.height,
              width: el.styles?.width,
              allStyles: el.styles
            });
          }
        });
      } else if (parsedContent.type === 'video') {
        console.log(`🎥 [GENERATE] Single video element from saved element:`, {
          aspectRatio: parsedContent.styles?.aspectRatio,
          height: parsedContent.styles?.height,
          width: parsedContent.styles?.width,
          allStyles: parsedContent.styles
        });
      }

      // Use the same HTML generation logic as the OffersPage
      if (parsedContent.elements && Array.isArray(parsedContent.elements)) {
        htmlContent = generateHTML(parsedContent);
      } else if (parsedContent.type === 'video') {
        // Handle single video element
        htmlContent = generateElementHTML(parsedContent);
      } else {
        // Fallback to string content
        htmlContent = element.content;
      }
    } catch (error) {
      console.error('Error parsing element content:', error);
      htmlContent = element.content || '';
    }

    // Generate affiliate link for the offer
    const affiliateUrl = generateAffiliateLink(offer);

    // Replace any existing href attributes in buttons/links with affiliate URL
    const affiliateHTML = htmlContent.replace(
      /href=["']([^"']*)["']/g,
      `href="${affiliateUrl}"`
    );

    return affiliateHTML;
  };

  // Generate HTML for all pages of a paginated offer element
export const generateAllPagesHTML = (element, offer, generateAffiliateLink) => {
    if (!element || !element.content) {
      return '';
    }

    if (!offer || !offer._id) {
      return element.content || '';
    }

    try {
      let parsedContent = element.content;
      if (typeof element.content === 'string') {
        parsedContent = JSON.parse(element.content);
      }

      // Check if this is a paginated element
      if (parsedContent.pagination && parsedContent.pagination.enabled && parsedContent.pagination.pages && parsedContent.pagination.pages.length > 0) {
        const allElements = parsedContent.elements || [];
        let allPagesHtml = '';

        // Generate HTML for all pages
        parsedContent.pagination.pages.forEach((pageInfo, pageIndex) => {
          const pageElements = allElements.slice(
            pageIndex === 0 ? 0 : parsedContent.pagination.pages.slice(0, pageIndex).reduce((sum, p) => sum + p.elementCount, 0),
            parsedContent.pagination.pages.slice(0, pageIndex + 1).reduce((sum, p) => sum + p.elementCount, 0)
          );

          // Add page separator (except for first page)
          if (pageIndex > 0) {
            allPagesHtml += `<div style="border-top: 2px dashed #ddd; margin: 20px 0; padding-top: 20px;"></div>`;
          }

          // Generate HTML for this page's elements
          pageElements.forEach(elementItem => {
            let elementHtml = '';

            // Use centralized HTML generation for all element types
            elementHtml = generateElementHTML(elementItem);

            // Replace any href with affiliate link
            if (elementHtml) {
              const affiliateUrl = generateAffiliateLink(offer);
              elementHtml = elementHtml.replace(
                /href=["']([^"']*)["']/g,
                `href="${affiliateUrl}"`
              );
            }

            allPagesHtml += elementHtml;
          });
        });

        return allPagesHtml;
      } else {
        // Not paginated, return full HTML
        return generateAffiliateElementHTML(element, offer);
      }
    } catch (error) {
      console.error('Error generating all pages HTML:', error);
      return generateAffiliateElementHTML(element, offer);
    }
  };

  // Generate HTML for a specific page of a paginated offer element
export const generatePageHTML = (element, offer, pageIndex = 0, generateAffiliateLink) => {
    if (!element || !element.content) {
      return '';
    }

    if (!offer || !offer._id) {
      return element.content || '';
    }

    try {
      let parsedContent = element.content;
      if (typeof element.content === 'string') {
        parsedContent = JSON.parse(element.content);
      }

      // Check if this is a paginated element
      if (parsedContent.pagination && parsedContent.pagination.enabled && parsedContent.pagination.pages && parsedContent.pagination.pages.length > 0) {
        const allElements = parsedContent.elements || [];
        const totalPages = parsedContent.pagination.pages.length;

        // Validate page index
        const validPageIndex = Math.max(0, Math.min(pageIndex, totalPages - 1));
        const pageInfo = parsedContent.pagination.pages[validPageIndex];

        // Calculate element range for this page
        const startIndex = validPageIndex === 0 ? 0 : parsedContent.pagination.pages.slice(0, validPageIndex).reduce((sum, p) => sum + p.elementCount, 0);
        const endIndex = startIndex + pageInfo.elementCount;
        const pageElements = allElements.slice(startIndex, endIndex);

        // Generate HTML for this page's elements
        let pageHtmlContent = '';
        pageElements.forEach(elementItem => {
          let elementHtml = '';

          // Use centralized HTML generation for all element types
          elementHtml = generateElementHTML(elementItem);

          // Replace any href with affiliate link
          if (elementHtml) {
            const affiliateUrl = generateAffiliateLink(offer);
            elementHtml = elementHtml.replace(
              /href=["']([^"']*)["']/g,
              `href="${affiliateUrl}"`
            );
          }

          pageHtmlContent += elementHtml;
        });

        return pageHtmlContent;
      } else {
        // Not paginated, return full HTML
        return generateAffiliateElementHTML(element, offer, generateAffiliateLink);
      }
    } catch (error) {
      console.error('Error generating page HTML:', error);
      return generateAffiliateElementHTML(element, offer, generateAffiliateLink);
    }
  };

  // Generate HTML for the first page of a paginated offer element (for backward compatibility)
export const generateFirstPageHTML = (element, offer, generateAffiliateLink) => {
    return generatePageHTML(element, offer, 0, generateAffiliateLink);
  };

  // Get total number of pages for an element
export const getTotalPages = (element) => {
    if (!element || !element.content) {
      return 1;
    }

    try {
      let parsedContent = element.content;
      if (typeof element.content === 'string') {
        parsedContent = JSON.parse(element.content);
      }

      if (parsedContent.pagination && parsedContent.pagination.enabled && parsedContent.pagination.pages) {
        return parsedContent.pagination.pages.length;
      }
    } catch (error) {
      console.error('Error getting total pages:', error);
    }

    return 1;
  };

  // Convert offer element content to blog elements array for MobileBlogPreview
  // Use the generated HTML directly as an offer element to preserve all content and styling
export const convertOfferElementToBlogElements = (element, offer, pageIndex = 0, generateAffiliateLink) => {
    if (!element || !element.content) {
      return [];
    }

    try {
      // Generate the HTML for the current page using the existing function
      const htmlContent = generatePageHTML(element, offer, pageIndex, generateAffiliateLink);

      if (!htmlContent) {
        return [];
      }

      // Return a single offer element with the generated HTML
      // This preserves all the HTML structure, styling, and content
      return [{
        id: element._id || `offer-element-${Date.now()}`,
        type: 'offer',
        content: 'OFFER_ELEMENT',
        htmlContent: htmlContent,
        offerId: offer?._id,
        styles: {}
      }];
    } catch (error) {
      console.error('Error converting offer element to blog elements:', error);
      return [];
    }
  };

export const generateHTML = (templateData) => {
    const elementsHTML = templateData.elements.map(element => {
      if (!element || !element.type) {
        return '';
      }
      return generateElementHTML(element);
    }).filter(html => html !== '').join('\n');

    return elementsHTML;
  };

  // Helper function to render list icons
export const getListIconHTML = (icon) => {
    switch (icon) {
      case 'check':
        return '<svg style="color: #b6ff3c; width: 2rem; height: 2rem;" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>';
      case 'star':
        return '<svg style="color: #ffe066; width: 2rem; height: 2rem;" viewBox="0 0 24 24" fill="currentColor"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>';
      case 'info':
        return '<svg style="color: #40c4ff; width: 2rem; height: 2rem;" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>';
      case 'warning':
        return '<svg style="color: #ffb347; width: 2rem; height: 2rem;" viewBox="0 0 24 24" fill="currentColor"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/></svg>';
      case 'red-x':
        return '<span style="color: #e53e3e; font-size: 1.5rem; font-weight: bold;">✗</span>';
      case 'pink-check':
        return '<span style="display: inline-block; width: 24px; height: 24px; border-radius: 50%; background-color: #ff9a9e; color: white; text-align: center; line-height: 24px; font-size: 1rem; font-weight: bold;">✓</span>';
      case 'dark-check':
        return '<span style="display: inline-block; width: 24px; height: 24px; border-radius: 4px; background-color: #2d3748; color: white; text-align: center; line-height: 24px; font-size: 1rem; font-weight: bold;">✓</span>';
      case 'green-badge':
        return '<span style="display: inline-block; width: 32px; height: 32px; border-radius: 50%; background-color: #68d391; color: white; text-align: center; line-height: 32px; font-size: 1.2rem; font-weight: bold; border: 2px solid #48bb78;">✓</span>';
      case 'star-badge':
        return '<span style="display: inline-block; width: 32px; height: 32px; border-radius: 50%; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; text-align: center; line-height: 32px; font-size: 1.2rem; font-weight: bold; border: 2px solid #5a67d8;">⭐</span>';
      case 'verified':
        return '<svg style="color: #4caf50; width: 1.5rem; height: 1.5rem;" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>';
      default:
        return '<svg style="color: #b6ff3c; width: 2rem; height: 2rem;" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>';
    }
  };

export const generateElementHTML = (element) => {
    const styles = element.styles || {};
    const enhancedStyles = { ...styles };

    // Apply styling based on element type
    switch (element.type) {
      case 'heading':
        enhancedStyles.marginBottom = enhancedStyles.margin || '16px';
        enhancedStyles.fontFamily = enhancedStyles.fontFamily || 'inherit';
        enhancedStyles.fontWeight = enhancedStyles.fontWeight || 'inherit';
        enhancedStyles.textAlign = enhancedStyles.textAlign || 'inherit';
        enhancedStyles.color = enhancedStyles.color || 'inherit';
        break;
      case 'text':
        enhancedStyles.marginBottom = '0';
        enhancedStyles.whiteSpace = 'pre-wrap';
        enhancedStyles.fontFamily = enhancedStyles.fontFamily || 'inherit';
        enhancedStyles.width = '100%';
        enhancedStyles.margin = enhancedStyles.margin || '0';
        break;
    }

    const styleString = Object.entries(enhancedStyles)
      .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
      .join('; ');

    switch (element.type) {
      case 'heading':
        return `<h1 style="${styleString}">${element.content}</h1>`;
      case 'text':
        return `<p style="${styleString}">${element.content || ''}</p>`;
      case 'image':
      case 'fittable_image':
        let imageSrc = element.content;
        if (element.imageSrc) {
          imageSrc = element.imageSrc;
        }
        // Preserve original aspect ratio exactly as stored - don't let styleString override it
        const imageStyleObject = { ...enhancedStyles };
        // Ensure aspect ratio from element.styles is preserved if it exists
        if (element.styles?.aspectRatio) {
          imageStyleObject.aspectRatio = element.styles.aspectRatio;
        }
        const imageStyleString = Object.entries(imageStyleObject)
          .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
          .join('; ');
        return `<div style="text-align: ${element.styles?.textAlign || 'center'}"><img src="${imageSrc}" alt="${element.alt || ''}" style="${imageStyleString}" /></div>`;
      case 'croppable_image':
        return element.croppedImage
          ? `<img src="${element.croppedImage}" alt="Cropped" style="width:${element.styles?.width || 300}px;height:${element.styles?.height || 200}px;object-fit:cover;display:block;margin:auto;border-radius:${element.styles?.borderRadius || '8px'};" />`
          : '';
      case 'CIRCULAR_IMAGE':
      case 'circular_image':
        return element.croppedImage
          ? `<img src="${element.croppedImage}" alt="Cropped" style="width:${element.styles?.width || 200}px;height:${element.styles?.height || 200}px;border-radius:50%;object-fit:cover;display:block;margin:auto;" />`
          : '';
      case 'media':
      case 'video_with_button_overlay': {
        const videoWidth = element.styles?.width || '100%';
        const videoHeight = element.styles?.height || 315;

        let paddingBottomPercent = 100;
        if (element.styles?.aspectRatio && element.styles.aspectRatio !== 'auto') {
          const [widthRatio, heightRatio] = element.styles.aspectRatio.split('/').map(Number);
          if (widthRatio && heightRatio) {
            paddingBottomPercent = (heightRatio / widthRatio) * 100;
          }
        } else {
          const heightNum = typeof videoHeight === 'number' ? videoHeight : (typeof videoHeight === 'string' ? parseInt(videoHeight) : 315);
          paddingBottomPercent = (heightNum / 400) * 100;
        }

        // Robust media URL extraction
        const mediaUrl = (element.videoFile && !element.videoFile.includes('Click Here')) ? element.videoFile :
          ((element.mediaUrl && !element.mediaUrl.includes('Click Here')) ? element.mediaUrl :
            ((element.videoSrc && !element.videoSrc.includes('Click Here')) ? element.videoSrc :
              ((element.content && !element.content.includes('Click Here')) ? element.content : null)));

        const isVideo = mediaUrl?.match(/\.(mp4|avi|mov|wmv|flv|webm)($|\?)/i);
        const isYouTube = mediaUrl?.includes('youtube.com') || mediaUrl?.includes('youtu.be');

        let videoContent = '';
        if (mediaUrl) {
          if (isVideo) {
            videoContent = `
              <video 
                src="${mediaUrl}" 
                style="position: absolute; top: 0; left: 0; width: 100%; max-width: 100%; height: 100%; margin: 0; padding: 0; object-fit: contain;" 
                controls 
                playsinline
                webkit-playsinline="true"
                preload="metadata"
                ${element.thumbnailUrl ? `poster="${element.thumbnailUrl}"` : ''}
              ></video>
            `;
          } else if (isYouTube) {
            const getYouTubeID = (url) => {
              if (!url) return '';
              const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
              const match = url.match(regExp);
              return (match && match[2].length === 11) ? match[2] : null;
            };
            const videoId = getYouTubeID(mediaUrl);
            const embedUrl = videoId ? `https://www.youtube.com/embed/${videoId}` : mediaUrl;
            videoContent = `
              <iframe 
                src="${embedUrl}" 
                style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none;" 
                allowfullscreen
              ></iframe>
            `;
          } else {
            videoContent = `
              <img 
                src="${mediaUrl}" 
                style="position: absolute; top: 0; left: 0; width: 100%; max-width: 100%; height: 100%; margin: 0; padding: 0; object-fit: contain;" 
              />
            `;
          }
        } else {
          videoContent = `
            <div style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background-color: #000; color: #fff;">
              <p>No media uploaded</p>
            </div>
          `;
        }

        const showOverlay = element.showOverlayButton || element.type === 'video_with_button_overlay';
        let overlayButtonHtml = '';

        if (showOverlay) {
          const buttonStyles = {
            ...(element.styles || {}),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '44px',
            minHeight: '44px',
            textAlign: 'center',
            textDecoration: 'none',
            boxSizing: 'border-box'
          };

          const buttonStyleString = Object.entries(buttonStyles)
            .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
            .join('; ');

          overlayButtonHtml = `
            <div style="position: absolute; bottom: 32px; left: 50%; transform: translateX(-50%); width: 55%; z-index: 100; display: flex; justify-content: center;">
              <a href="${element.url || '#'}" target="_blank" style="${buttonStyleString}">${element.content || 'Click Here'}</a>
            </div>
          `;
        }

        return `
          <div style="text-align: center; margin-bottom: 24px; width: calc(100% + 32px); max-width: calc(100% + 32px); padding: 0; margin-left: -16px; margin-right: -16px; position: relative;">
            <div style="position: relative; width: 100%; max-width: 100%; padding-bottom: ${paddingBottomPercent}%; height: 0; overflow: hidden; margin: 0; padding-left: 0; padding-right: 0; border-radius: 0; background-color: #000;">
              ${videoContent}
              ${overlayButtonHtml}
            </div>
          </div>
        `;
      }
      case 'video':
        // Prioritize videoFile (S3 URL) over content (YouTube URL)
        let videoSrc = element.videoFile || element.videoSrc || element.content;

        // Handle different video source formats
        if (videoSrc && !videoSrc.startsWith('http') && !videoSrc.startsWith('data:')) {
          // If it's a relative path, try to make it absolute
          if (videoSrc.startsWith('/uploads/') || videoSrc.startsWith('uploads/')) {
            videoSrc = `http://localhost:8080${videoSrc.startsWith('/') ? '' : '/'}${videoSrc}`;
          } else if (videoSrc.startsWith('/images/') || videoSrc.startsWith('images/')) {
            videoSrc = `http://localhost:3000${videoSrc.startsWith('/') ? '' : '/'}${videoSrc}`;
          } else {
            videoSrc = `http://localhost:8080${videoSrc.startsWith('/') ? '' : '/'}${videoSrc}`;
          }
        }

        // If it's a YouTube embed URL, don't render as video element
        if (videoSrc && videoSrc.includes('youtube.com/embed')) {
          return `<div style="text-align: ${element.styles?.textAlign || 'center'}; padding: 20px; background-color: #f5f5f5; border-radius: 8px;">
            <p style="color: #666; margin: 0;">YouTube video: ${videoSrc}</p>
          </div>`;
        }

        // Use the same logic as OfferPageBuilderSimple to preserve aspect ratio correctly
        const videoWidth = element.styles?.width || '100%';
        const videoHeight = element.styles?.height || 315;

        console.log('🎥 [VIDEO HTML] Generating video HTML:', {
          videoWidth,
          videoHeight,
          aspectRatio: element.styles?.aspectRatio,
          allStyles: element.styles
        });

        if (videoWidth === '100%' || videoWidth === 'auto') {
          // Check if aspectRatio is explicitly set in styles (e.g., "1/1" for square)
          let paddingBottomPercent;
          if (element.styles?.aspectRatio && element.styles.aspectRatio !== 'auto') {
            // Parse aspect ratio string (e.g., "1/1", "16/9", "9/16")
            const [widthRatio, heightRatio] = element.styles.aspectRatio.split('/').map(Number);
            console.log('🎥 [VIDEO HTML] Using aspectRatio from styles:', {
              aspectRatio: element.styles.aspectRatio,
              widthRatio,
              heightRatio,
              calculatedPaddingBottom: (heightRatio / widthRatio) * 100
            });
            if (widthRatio && heightRatio) {
              // Calculate padding-bottom percentage: (height / width) * 100%
              paddingBottomPercent = (heightRatio / widthRatio) * 100;
            } else {
              // Invalid aspect ratio, fall back to height calculation
              const heightNum = typeof videoHeight === 'number' ? videoHeight : (typeof videoHeight === 'string' ? parseInt(videoHeight) : 315);
              paddingBottomPercent = (heightNum / 400) * 100;
              console.log('🎥 [VIDEO HTML] Invalid aspectRatio, using height fallback:', paddingBottomPercent);
            }
          } else {
            // No aspectRatio set, try to detect from height/width ratio
            const heightNum = typeof videoHeight === 'number' ? videoHeight : (typeof videoHeight === 'string' ? parseInt(videoHeight) : 315);
            const baseWidth = 400; // Base width for 100% videos

            console.log('🎥 [VIDEO HTML] No aspectRatio in styles, detecting from height/width:', {
              heightNum,
              baseWidth,
              difference: Math.abs(heightNum - baseWidth)
            });

            // If height equals width (or very close), treat as square (1:1)
            // Allow 5px tolerance for rounding differences
            if (Math.abs(heightNum - baseWidth) <= 5) {
              paddingBottomPercent = 100; // Square: 1/1 = 100%
              console.log('🎥 [VIDEO HTML] Detected square (height ≈ width), using 100%');
            } else {
              // Calculate from height/width ratio
              paddingBottomPercent = (heightNum / baseWidth) * 100;
              console.log('🎥 [VIDEO HTML] Calculated from height/width ratio:', paddingBottomPercent);
            }
          }

          console.log('🎥 [VIDEO HTML] Final paddingBottomPercent:', paddingBottomPercent);

          return `<div style="text-align: ${element.styles?.textAlign || 'center'}; display: block; width: 100%; max-width: 100%; margin-left: 0; margin-right: 0; padding-left: 0; padding-right: 0;">
            <div class="video-aspect-ratio-container" style="position: relative !important; width: 100% !important; max-width: 100% !important; padding-bottom: ${paddingBottomPercent}% !important; height: 0 !important; display: block !important; margin: 0 !important; padding-left: 0 !important; padding-right: 0 !important; padding-top: 0 !important;">
              <video 
                src="${videoSrc}" 
                style="position: absolute !important; top: 0 !important; left: 0 !important; width: 100% !important; height: 100% !important; object-fit: cover !important; margin: 0 !important; padding: 0 !important; max-width: 100% !important;" 
                controls 
                preload="metadata"
                ${element.thumbnailUrl ? `poster="${element.thumbnailUrl}"` : ''}
              ></video>
            </div>
          </div>`;
        } else {
          // Use original styles if width is not 100%
          const videoStyleString = Object.entries(element.styles || {})
            .filter(([key]) => key !== 'aspectRatio')
            .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
            .join('; ');
          return `<div style="text-align: ${element.styles?.textAlign || 'center'}; width: 100%; max-width: 100%; margin-left: 0; margin-right: 0; padding-left: 0; padding-right: 0;"><video src="${videoSrc}" controls style="${videoStyleString}; width: 100%; max-width: 100%; margin: 0; padding: 0;" ${element.thumbnailUrl ? `poster="${element.thumbnailUrl}"` : ''} /></div>`;
        }

      case 'button':
        let buttonContent = element.content;
        let buttonStyles = enhancedStyles;

        if (element.styles?.textTransform === 'titlecase') {
          buttonContent = element.content.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
          delete buttonStyles.textTransform;
          const buttonStyleString = Object.entries(buttonStyles)
            .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
            .join('; ');
          return `<div style="text-align: ${element.styles?.textAlign || 'center'}"><a href="${element.url}" style="${buttonStyleString}">${buttonContent}</a></div>`;
        }

        return `<div style="text-align: ${element.styles?.textAlign || 'center'}"><a href="${element.url || '#'}" style="${styleString}">${buttonContent}</a></div>`;
      case 'comparison_table': {
        const titlePreset = getPreset(element.titleStylePreset || 'compareTableTableTitle') || {};
        const productNamePreset = getPreset(element.productNameStylePreset || 'compareTableProdctName') || {};
        const featureTitlePreset = getPreset(element.featureTitleStylePreset || 'compareTableFeatureTitle') || {};
        const featureDescPreset = getPreset(element.featureDescStylePreset || 'compareTableFeatureDescription') || {};

        const comparisonTableHTML = `
          <div style="margin-bottom: 4px;">
            ${element.title ? `<h3 style="text-align: ${titlePreset.textAlign || 'center'}; margin-bottom: 20px; font-family: ${titlePreset.fontFamily || 'inherit'}; font-size: ${titlePreset.fontSize || 'inherit'}; font-weight: ${titlePreset.fontWeight || 'bold'}; color: ${titlePreset.color || 'inherit'}; line-height: ${titlePreset.lineHeight || 'inherit'}; letter-spacing: ${titlePreset.letterSpacing || 'inherit'};">${element.title}</h3>` : ''}
            <table style="width: 100%; border-collapse: collapse; box-shadow: 0 2px 8px rgba(0,0,0,0.1); border-radius: 8px; overflow: hidden;">
              <thead>
                ${(element.productAImage || element.productBImage) ? `
                  <tr>
                    <td style="width: 40%; border: none; padding: 8px;"></td>
                    <td style="width: 30%; border: none; padding: 8px; text-align: center; background-color: #B8E6E1;">
                      ${element.productAImage ? `<img src="${element.productAImage}" alt="${element.productA}" style="width: 100px; height: 100px; object-fit: cover; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);" />` : ''}
                    </td>
                    <td style="width: 30%; border: none; padding: 8px; text-align: center; background-color: #ffebee;">
                      ${element.productBImage ? `<img src="${element.productBImage}" alt="${element.productB}" style="width: 100px; height: 100px; object-fit: cover; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);" />` : ''}
                    </td>
                  </tr>
                ` : ''}
                <tr>
                  <th style="padding: 16px; font-weight: bold; font-size: 16px; width: 40%; background-color: #f5f5f5; text-align: left;">Feature</th>
                  <th style="padding: 16px; width: 30%; background-color: #B8E6E1; text-align: ${productNamePreset.textAlign || 'center'}; font-family: ${productNamePreset.fontFamily || 'inherit'}; font-size: ${productNamePreset.fontSize || '16px'}; font-weight: ${productNamePreset.fontWeight || 'bold'}; color: ${productNamePreset.color || '#2D5A5A'}; line-height: ${productNamePreset.lineHeight || 'inherit'}; letter-spacing: ${productNamePreset.letterSpacing || 'inherit'};">${element.productA}</th>
                  <th style="padding: 16px; width: 30%; background-color: #ffebee; text-align: ${productNamePreset.textAlign || 'center'}; font-family: ${productNamePreset.fontFamily || 'inherit'}; font-size: ${productNamePreset.fontSize || '16px'}; font-weight: ${productNamePreset.fontWeight || 'bold'}; color: ${productNamePreset.color || '#c62828'}; line-height: ${productNamePreset.lineHeight || 'inherit'}; letter-spacing: ${productNamePreset.letterSpacing || 'inherit'};">${element.productB}</th>
                </tr>
              </thead>
              <tbody>
                ${(element.rows || []).map((row, index) => `
                  <tr style="background-color: ${index % 2 === 0 ? '#fff' : '#f9f9f9'};">
                    <td style="padding: 16px; vertical-align: top;">
                      <div style="margin-bottom: 4px; font-family: ${featureTitlePreset.fontFamily || 'inherit'}; font-size: ${featureTitlePreset.fontSize || 'inherit'}; font-weight: ${featureTitlePreset.fontWeight || 'medium'}; color: ${featureTitlePreset.color || 'inherit'}; text-align: ${featureTitlePreset.textAlign || 'left'}; line-height: ${featureTitlePreset.lineHeight || 'inherit'}; letter-spacing: ${featureTitlePreset.letterSpacing || 'inherit'};">${row.feature}</div>
                      ${row.description ? `<div style="font-family: ${featureDescPreset.fontFamily || 'inherit'}; font-size: ${featureDescPreset.fontSize || '14px'}; font-weight: ${featureDescPreset.fontWeight || 'inherit'}; color: ${featureDescPreset.color || '#666'}; text-align: ${featureDescPreset.textAlign || 'left'}; line-height: ${featureDescPreset.lineHeight || 'inherit'}; letter-spacing: ${featureDescPreset.letterSpacing || 'inherit'};">${row.description}</div>` : ''}
                    </td>
                    <td style="padding: 16px; text-align: center; background-color: ${row.productA ? '#B8E6E1' : '#fff'};">
                      ${row.productA ? '✓' : '✗'}
                    </td>
                    <td style="padding: 16px; text-align: center; background-color: ${row.productB ? '#B8E6E1' : '#ffebee'};">
                      ${row.productB ? '✓' : '✗'}
                    </td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        `;
        return comparisonTableHTML;
      }
      case 'two_column_comparison': {
        const columnTitlePreset = getPreset(element.titleStylePreset || 'compareTableTwoColumnColumnTitle') || {};
        const itemPreset = getPreset(element.itemStylePreset || 'compareTableTwoColumnItemTitle') || {};

        const twoColumnComparisonHTML = `
          <div style="margin-bottom: 4px;">
            <div style="display: flex; min-height: 400px; border-radius: 16px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
              <!-- Left Column - Negative Points -->
              <div style="flex: 1; background-color: #fff; border: 1px solid #e0e0e0; border-right: 0.5px solid #e0e0e0; padding: 24px; display: flex; flex-direction: column;">
                ${element.leftColumnImage ? `
                  <div style="display: flex; justify-content: center; margin-bottom: 16px;">
                    <img src="${element.leftColumnImage}" alt="${element.leftColumnTitle}" style="width: 80px; height: 80px; object-fit: cover; border-radius: 8px;" />
                  </div>
                ` : ''}
                <h3 style="text-align: ${columnTitlePreset.textAlign || 'center'}; margin-bottom: 24px; font-family: ${columnTitlePreset.fontFamily || 'inherit'}; font-size: ${columnTitlePreset.fontSize || '16px'}; font-weight: ${columnTitlePreset.fontWeight || 'bold'}; color: ${columnTitlePreset.color || '#000'}; line-height: ${columnTitlePreset.lineHeight || 'inherit'}; letter-spacing: ${columnTitlePreset.letterSpacing || 'inherit'};">${element.leftColumnTitle}</h3>
                <div style="flex-grow: 1;">
                  ${(element.leftItems || []).map(item => `
                    <div style="display: flex; align-items: flex-start; margin-bottom: 16px; gap: 16px;">
                      <svg style="width: 20px; height: 20px; margin-top: 2px; flex-shrink: 0;" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" fill="#dc3545"/>
                        <path d="M15.59 7L12 10.59 8.41 7 7 8.41 10.59 12 7 15.59 8.41 17 12 13.41 15.59 17 17 15.59 13.41 12z" fill="white"/>
                      </svg>
                      <div style="font-family: ${itemPreset.fontFamily || 'inherit'}; font-size: ${itemPreset.fontSize || '12px'}; font-weight: ${itemPreset.fontWeight || '500'}; color: ${itemPreset.color || '#000'}; text-align: ${itemPreset.textAlign || 'left'}; line-height: ${itemPreset.lineHeight || '1.4'}; letter-spacing: ${itemPreset.letterSpacing || 'inherit'};">${item}</div>
                    </div>
                  `).join('')}
                </div>
              </div>
              <!-- Right Column - Positive Points -->
              <div style="flex: 1; background-color: #f0f9f0; border: 1px solid #e0e0e0; border-left: 0.5px solid #e0e0e0; padding: 24px; display: flex; flex-direction: column;">
                ${element.rightColumnImage ? `
                  <div style="display: flex; justify-content: center; margin-bottom: 16px;">
                    <img src="${element.rightColumnImage}" alt="${element.rightColumnTitle}" style="width: 80px; height: 80px; object-fit: cover; border-radius: 8px;" />
                  </div>
                ` : ''}
                <h3 style="text-align: ${columnTitlePreset.textAlign || 'center'}; margin-bottom: 24px; font-family: ${columnTitlePreset.fontFamily || 'inherit'}; font-size: ${columnTitlePreset.fontSize || '16px'}; font-weight: ${columnTitlePreset.fontWeight || 'bold'}; color: ${columnTitlePreset.color || '#000'}; line-height: ${columnTitlePreset.lineHeight || 'inherit'}; letter-spacing: ${columnTitlePreset.letterSpacing || 'inherit'};">${element.rightColumnTitle}</h3>
                <div style="flex-grow: 1;">
                  ${(element.rightItems || []).map(item => `
                    <div style="display: flex; align-items: flex-start; margin-bottom: 16px; gap: 16px;">
                      <svg style="color: #28a745; width: 20px; height: 20px; margin-top: 2px; flex-shrink: 0;" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                      <div style="font-family: ${itemPreset.fontFamily || 'inherit'}; font-size: ${itemPreset.fontSize || '12px'}; font-weight: ${itemPreset.fontWeight || '500'}; color: ${itemPreset.color || '#000'}; text-align: ${itemPreset.textAlign || 'left'}; line-height: ${itemPreset.lineHeight || '1.4'}; letter-spacing: ${itemPreset.letterSpacing || 'inherit'};">${item}</div>
                    </div>
                  `).join('')}
                </div>
              </div>
            </div>
          </div>
        `;
        return twoColumnComparisonHTML;
      }
      case 'BUTTON_WITH_NOTICE': {
        const getVariantStylesHTML = (variant, styleVariant = 'default') => {
          const styleVariants = {
            'urgent-sale': {
              'default': {
                backgroundColor: '#ff69b4',
                noticeColor: '#101022',
                buttonBg: '#101022',
                buttonColor: '#ffffff',
                border: 'none',
                borderRadius: '8px'
              },
              'modern': {
                backgroundColor: '#ffffff',
                noticeColor: '#101022',
                buttonBg: '#ff69b4',
                buttonColor: '#101022',
                border: '2px solid #ff69b4',
                borderRadius: '12px'
              },
              'minimal': {
                backgroundColor: '#fafafa',
                noticeColor: '#101022',
                buttonBg: '#ff69b4',
                buttonColor: '#101022',
                border: '1px solid #e0e0e0',
                borderRadius: '4px'
              },
              'gradient': {
                backgroundColor: 'linear-gradient(135deg, #ff69b4, #ff1493)',
                noticeColor: '#ffffff',
                buttonBg: '#ffffff',
                buttonColor: '#101022',
                border: 'none',
                borderRadius: '16px'
              }
            },
            'membership-quiz': {
              'default': {
                backgroundColor: '#0000ff',
                noticeColor: '#ffffff',
                buttonBg: '#ffffff',
                buttonColor: '#0000ff',
                border: 'none',
                borderRadius: '8px'
              },
              'elegant': {
                backgroundColor: '#000080',
                noticeColor: '#ffffff',
                buttonBg: '#ffffff',
                buttonColor: '#000080',
                border: '2px solid #4169e1',
                borderRadius: '12px'
              },
              'soft': {
                backgroundColor: '#e6f3ff',
                noticeColor: '#0000ff',
                buttonBg: '#0000ff',
                buttonColor: '#ffffff',
                border: '1px solid #b3d9ff',
                borderRadius: '8px'
              },
              'corporate': {
                backgroundColor: '#ffffff',
                noticeColor: '#101022',
                buttonBg: '#0000ff',
                buttonColor: '#ffffff',
                border: '2px solid #e0e0e0',
                borderRadius: '6px'
              }
            },
            'media-buzz': {
              'default': {
                backgroundColor: '#ffffff',
                noticeColor: '#101022',
                buttonBg: '#ffff00',
                buttonColor: '#101022',
                border: '2px dashed #101022',
                borderRadius: '8px'
              },
              'news': {
                backgroundColor: '#f8f9fa',
                noticeColor: '#101022',
                buttonBg: '#0000ff',
                buttonColor: '#ffffff',
                border: '1px solid #e0e0e0',
                borderRadius: '8px'
              },
              'viral': {
                backgroundColor: '#ffff00',
                noticeColor: '#101022',
                buttonBg: '#101022',
                buttonColor: '#ffffff',
                border: '2px solid #ffd700',
                borderRadius: '12px'
              },
              'trending': {
                backgroundColor: '#e6f3ff',
                noticeColor: '#0000ff',
                buttonBg: '#0000ff',
                buttonColor: '#ffffff',
                border: '1px solid #b3d9ff',
                borderRadius: '10px'
              }
            }
          };

          return styleVariants[variant]?.[styleVariant] || styleVariants[variant]?.['default'] || {
            backgroundColor: '#ff69b4',
            noticeColor: '#101022',
            buttonBg: '#101022',
            buttonColor: '#ffffff',
            border: 'none',
            borderRadius: '8px'
          };
        };

        const variantStylesHTML = getVariantStylesHTML(element.variant, element.styleVariant);
        const boxShadow = element.styleVariant === 'gradient' ? '0 8px 32px rgba(255, 107, 107, 0.3)' : '0 2px 8px rgba(0,0,0,0.1)';

        return `
          <div style="background: ${variantStylesHTML.backgroundColor}; padding: 20px; border-radius: ${variantStylesHTML.borderRadius}; text-align: center; margin-bottom: 4px; border: ${variantStylesHTML.border}; box-shadow: ${boxShadow};">
            <p style="color: ${variantStylesHTML.noticeColor}; font-weight: bold; margin-bottom: 16px; line-height: 1.5;">
              ${element.content?.notice?.replace(/\n/g, '<br>') || ''}
            </p>
            <p style="color: ${variantStylesHTML.noticeColor}; margin-bottom: 16px;">
              ${element.content?.callToAction || ''}
            </p>
            <a href="${element.content?.buttonUrl || '#'}" style="background-color: ${variantStylesHTML.buttonBg}; color: ${variantStylesHTML.buttonColor}; padding: 16px 32px; font-size: 18px; font-weight: bold; border: none; border-radius: 8px; text-decoration: none; display: inline-block; max-width: 400px; width: 100%; box-shadow: 0 2px 4px rgba(0,0,0,0.1); transition: all 0.2s ease;">
              ${element.content?.buttonText || ''}
            </a>
          </div>
        `;
      }
      case 'Button + headline + Product Image': {
        return `
          <div style="background: ${element.content?.background || '#ffffff'}; color: ${element.content?.textColor || '#333333'}; border-radius: 16px; margin-bottom: 4px; text-align: center; padding: 20px; box-shadow: 0 2px 12px rgba(0,0,0,0.07);">
            <h2 style="color: ${element.content?.textColor || '#333333'}; font-weight: bold; margin-bottom: 16px; font-size: 1.5rem;">
              ${element.content?.headline || 'Amazing Product Offer'}
            </h2>
            <div style="margin-bottom: 16px;">
              <img src="${element.content?.productImage || ''}" alt="Product" style="width: 100%; max-width: 400px; height: auto; border-radius: 8px; border: 2px dashed #e0e0e0;" />
            </div>
            ${element.content?.subheadline ? `
              <p style="color: ${element.content?.textColor || '#333333'}; margin-bottom: 16px; line-height: 1.5;">
                ${element.content.subheadline.replace(/\n/g, '<br>')}
              </p>
            ` : ''}
            <a href="${element.content?.buttonUrl || '#'}" style="background-color: ${element.content?.buttonColor || '#4caf50'}; color: #fff; padding: 18px 0; font-size: 1.3rem; font-weight: bold; border: none; border-radius: 8px; text-decoration: none; display: block; width: 100%; margin: 16px 0; box-shadow: 0 2px 4px rgba(0,0,0,0.1); transition: all 0.2s ease;">
              ${element.content?.buttonText || 'Get This Offer Now!'}
            </a>
          </div>
        `;
      }
      case 'TEXT_WITH_RIGHT_IMAGE':
        return `
          <div style="background-color: ${element.styles?.backgroundColor || '#fff'}; color: ${element.styles?.color || '#333'}; font-family: ${element.styles?.fontFamily || 'Inter, Arial, sans-serif'}; font-size: ${element.styles?.fontSize || '1.1rem'}; border-radius: ${element.styles?.borderRadius || '12px'}; padding: ${element.styles?.padding || '20px'}; margin: ${element.styles?.margin || '0 0 16px 0'}; display: flex; align-items: center; gap: 24px;">
            <div style="flex: 1;">
              <h3 style="margin: 0; font-weight: bold; color: inherit;">${element.content?.text || ''}</h3>
            </div>
            <div style="flex-shrink: 0; width: 180px;">
              <img src="${element.content?.image || ''}" alt="Right side image" style="width: 100%; height: 120px; object-fit: cover; border-radius: 8px; border: 1px solid #e0e0e0;" />
            </div>
          </div>
        `;
      case 'LEFT_IMAGE_TEXT': {
        const alignItems = element.imageAlignment === 'top' ? 'flex-start' :
          element.imageAlignment === 'bottom' ? 'flex-end' : 'center';
        return `
          <div style="display: flex; gap: ${element.spacing || 20}px; margin-bottom: 4px; ${Object.entries(element.styles || {})
            .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
            .join('; ')}; align-items: ${alignItems};">
            <div style="flex-shrink: 0;">
              <img src="${element.content?.image || ''}" alt="Profile image" style="width: ${element.imageSize || 120}px; height: ${element.imageSize || 120}px; object-fit: cover; border-radius: 50%; border: 3px solid #e0e0e0;" />
            </div>
            <div style="flex: 1;">
              <p style="margin: 0; color: ${element.textStyles?.color || 'inherit'}; line-height: ${element.textStyles?.lineHeight || '1.6'}; font-size: ${element.textStyles?.fontSize || '16px'}; font-weight: ${element.textStyles?.fontWeight || 'normal'}; font-family: ${element.textStyles?.fontFamily || 'inherit'}; text-align: ${element.textStyles?.textAlign || 'left'}; background-color: ${element.textStyles?.backgroundColor || 'transparent'}; padding: ${element.textStyles?.padding || '0px'}; border-radius: ${element.textStyles?.borderRadius || '0px'};">${element.content?.text || ''}</p>
            </div>
          </div>
        `;
      }
      case 'QUOTATION_BLOCK':
        return `
            <div style="
              position: relative;
              background: ${element.styles?.backgroundColor || '#fce7f3'};
              color: ${element.styles?.color || '#101022'};
              border-radius: ${element.styles?.borderRadius || '16px'};
              padding: ${element.styles?.padding || '24px 32px 24px 32px'};
              font-style: italic;
              min-height: 120px;
              margin-bottom: 16px;
              overflow: visible;
              font-size: ${element.styles?.fontSize || '18px'};
              font-weight: ${element.styles?.fontWeight || '400'};
              line-height: ${element.styles?.lineHeight || '1.5'};
              font-family: ${element.styles?.fontFamily || "'Inter', sans-serif"};
            ">
              <div style="
                position: absolute;
                top: -12px;
                left: 12px;
                width: 40px;
                height: 40px;
                background: #fff;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                z-index: 2;
              ">
                <span style="font-size: 24px; font-family: serif; font-weight: bold; color: #101022;">&ldquo;</span>
              </div>
              <span style="
                font-style: italic;
                font-size: ${element.styles?.fontSize || '18px'};
                font-weight: ${element.styles?.fontWeight || '400'};
                line-height: ${element.styles?.lineHeight || '1.5'};
                font-family: ${element.styles?.fontFamily || "'Inter', sans-serif"};
                display: block;
              ">
                ${element.content || ''}
              </span>
            </div>
          `;
      case 'layout':
        return generateLayoutHTML(element);
      case 'html':
        return element.content;
      case 'divider':
        return `<hr style="${styleString}" />`;
      case 'checklist':
      case 'CHECKLIST':
        const checklistHTML = `
            <div style="${styleString}">
              ${element.title ? `<h2 style="text-align: center; color: #1976d2; margin-bottom: 24px;">${element.title}</h2>` : ''}
              <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 16px;">
                ${(element.items || []).map(item => `
                  <div style="display: flex; align-items: center; gap: 8px; padding: 8px; background-color: #f5f5f5; border-radius: 4px; margin-bottom: 8px;">
                    ${getListIconHTML(item.icon || 'check')}
                    <span style="font-weight: 500; color: #333; flex: 1;">${(item.text || '').replace(/\n/g, '<br>')}</span>
                  </div>
                `).join('')}
              </div>
            </div>
          `;
        return checklistHTML;
      case 'CUSTOM_LIST':
      case 'CUSTOM_LIST_TWO_LINE':
      case 'TIMELINE_LIST':
      case 'PROCESS_STEPS':
        const listItems = element.rows || element.items || [];
        const backgroundColor = element.styles?.backgroundColor || '#181818';
        const textColor = element.styles?.color || '#fff';
        // If background is very dark, use light text; if light, use dark text
        const isDarkBackground = backgroundColor === '#181818' || backgroundColor.includes('rgb') && backgroundColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/)?.[1] < 128;
        const itemTextColor = isDarkBackground ? textColor : '#333';

        const listHTML = `
          <div style="background-color: ${backgroundColor}; 
                      color: ${textColor}; 
                      font-family: ${element.styles?.fontFamily || 'Inter, Arial, sans-serif'}; 
                      font-size: ${element.styles?.fontSize || '1.25rem'}; 
                      border-radius: ${element.styles?.borderRadius || '12px'}; 
                      padding: ${element.styles?.padding || '24px'}; 
                      margin: ${element.styles?.margin || '0 0 16px 0'}">
            ${element.title ? `<h3 style="margin-bottom: 16px; color: ${textColor};">${element.title}</h3>` : ''}
            <ul style="list-style: none; padding: 0; margin: 0;">
              ${listItems.map(item => `
                <li style="display: flex; align-items: flex-start; gap: 12px; margin-bottom: 12px;">
                  <span style="flex-shrink: 0;">${getListIconHTML(item.icon || 'check')}</span>
                  <span style="color: ${itemTextColor}; flex: 1;">${(item.text || item.content || '').replace(/\n/g, '<br>')}</span>
                </li>
              `).join('')}
            </ul>
          </div>
        `;
        return listHTML;
      case 'faq_accordion':
      case 'FAQ_ACCORDION':
        const faqItems = element.items || [];
        const faqTitle = element.title || '';
        const textAlign = element.textAlign || 'left';
        const questionTextColor = element.questionTextColor || '#4B329A';
        const answerTextColor = element.answerTextColor || '#4B329A';
        const titleTextColor = element.titleTextColor || '#1976d2';
        const questionBackgroundColor = element.questionBackgroundColor || 'transparent';
        const answerBackgroundColor = element.answerBackgroundColor || 'rgba(0, 0, 0, 0.02)';
        const itemBorderRadius = element.itemBorderRadius || '4px';
        const itemSpacing = element.itemSpacing || '4px';
        const questionPadding = element.questionPadding || '16px';
        const answerPadding = element.answerPadding || '16px';
        const showDivider = element.showDivider !== false;
        const dividerColor = element.dividerColor || '#C1A7F1';
        const iconColor = element.iconColor || '#36C3D6';
        const iconType = element.iconType || 'arrow';

        // Generate icon based on iconType (matching FAQAccordion component)
        const getIconHTML = () => {
          switch (iconType) {
            case 'plusMinus':
              return '○'; // Circle for plus/minus style
            case 'expandMore':
              return '▾'; // Larger downward chevron
            case 'arrowDrop':
              return '▼'; // Standard drop arrow
            case 'arrow':
            default:
              return '▼'; // Standard arrow
          }
        };
        const iconHTML = getIconHTML();

        // Generate unique ID for this FAQ instance
        const faqId = `faq_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        const faqHTML = `
          <style>
            .faq-answer-${faqId} {
              max-height: 0;
              overflow: hidden;
              transition: max-height 0.3s ease-out, margin-top 0.3s ease-out, padding 0.3s ease-out;
              margin-top: 0;
              padding: 0;
            }
            .faq-answer-${faqId}.open {
              max-height: 2000px;
              margin-top: 8px;
              padding: ${answerPadding};
              transition: max-height 0.5s ease-in, margin-top 0.3s ease-in, padding 0.3s ease-in;
            }
            .faq-question-${faqId} {
              cursor: pointer;
              user-select: none;
              transition: background-color 0.2s;
            }
            .faq-question-${faqId}:hover {
              background-color: ${questionBackgroundColor === 'transparent' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(0, 0, 0, 0.06)'} !important;
            }
            .faq-icon-${faqId} {
              display: inline-block;
              transition: transform 0.3s;
              color: ${iconColor};
              font-size: 20px;
              line-height: 1;
              margin-left: 8px;
              float: right;
              font-weight: normal;
            }
            .faq-icon-${faqId}.open {
              transform: rotate(180deg);
            }
          </style>
          <div style="margin: 32px 0; ${element.styles?.margin ? `margin: ${element.styles.margin};` : ''}">
            ${faqTitle ? `<h3 style="text-align: ${textAlign}; color: ${titleTextColor}; margin-bottom: 24px; font-weight: bold;">${faqTitle}</h3>` : ''}
            <div>
              ${faqItems.map((item, idx) => `
                <div style="margin-bottom: ${itemSpacing};">
                  <div class="faq-question-${faqId}" 
                       onclick="(function(e) {
                         const answer = e.currentTarget.nextElementSibling;
                         const icon = e.currentTarget.querySelector('.faq-icon-${faqId}');
                         if (answer.classList.contains('open')) {
                           answer.classList.remove('open');
                           if (icon) icon.classList.remove('open');
                         } else {
                           answer.classList.add('open');
                           if (icon) icon.classList.add('open');
                         }
                       })(event)"
                       style="background-color: ${questionBackgroundColor}; 
                              border-radius: ${itemBorderRadius}; 
                              padding: ${questionPadding}; 
                              text-align: ${textAlign};
                              position: relative;">
                    <div style="font-weight: bold; 
                                color: ${questionTextColor}; 
                                line-height: 1.4; 
                                font-size: 1.1rem;
                                padding-right: 40px;">
                      ${item.question || ''}
                      <span class="faq-icon-${faqId}">${iconHTML}</span>
                    </div>
                  </div>
                  <div class="faq-answer-${faqId}" 
                       style="background-color: ${answerBackgroundColor}; 
                              border-radius: ${itemBorderRadius}; 
                              text-align: ${textAlign};">
                    <div style="color: ${answerTextColor}; 
                                line-height: 1.6;">
                      ${item.answer || ''}
                    </div>
                  </div>
                  ${showDivider && idx < faqItems.length - 1 ? `<div style="border-top: 2px solid ${dividerColor}; margin-top: 16px;"></div>` : ''}
                </div>
              `).join('')}
            </div>
          </div>
        `;
        return faqHTML;
      default:
        return `<div>${element.content || ''}</div>`;
    }
  };

export const generateLayoutHTML = (layout) => {
    const layoutStyle = Object.entries(layout.styles || {})
      .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
      .join('; ');

    // Add safety check for zones
    if (!layout.zones || !Array.isArray(layout.zones)) {
      return `<div class="layout-container" style="${layoutStyle}">Layout with no zones</div>`;
    }

    // Determine if this is a multi-column layout
    const isMultiColumn = layout.layoutType === 'TWO_COLUMN' ||
      layout.layoutType === 'THREE_COLUMN' ||
      layout.layoutType === 'FEATURE' ||
      layout.layoutType === 'SINGLE_ROW_2' ||
      layout.layoutType === 'SINGLE_ROW_3' ||
      layout.layoutType === 'SINGLE_ROW_4' ||
      layout.layoutType === 'SINGLE_ROW_5' ||
      layout.layoutType === 'two_column' ||
      layout.layoutType === 'three_column' ||
      layout.layoutType === 'feature' ||
      layout.layoutType === 'single_row_2' ||
      layout.layoutType === 'single_row_3' ||
      layout.layoutType === 'single_row_4' ||
      layout.layoutType === 'single_row_5';

    const zonesHTML = layout.zones.map((zone, index) => {
      if (!zone) {
        return '<div class="zone" style="flex: 1; padding: 10px; min-width: 280px;">Invalid zone</div>';
      }

      const zoneElements = (zone.elements || []).map(zoneElement => {
        if (!zoneElement) {
          return '';
        }
        return generateElementHTML(zoneElement);
      }).join('\n');

      // Get zone width
      const zoneWidth = zone.width || '100%';

      const zoneStyle = isMultiColumn
        ? `flex: 1; min-width: 0; padding: 6px; text-align: ${zone.textAlign || 'left'}; word-wrap: break-word; overflow-wrap: break-word; box-sizing: border-box;`
        : `width: 100%; padding: 6px; text-align: ${zone.textAlign || 'left'}; word-wrap: break-word; overflow-wrap: break-word; box-sizing: border-box;`;

      return `<div class="zone" style="${zoneStyle}">${zoneElements}</div>`;
    }).join('\n');

    // Apply responsive flexDirection and flexWrap based on layout type
    let flexDirection = 'row';
    let flexWrap = 'nowrap';
    if (layout.layoutType === 'THREE_COLUMN' || layout.layoutType === 'three_column') {
      flexDirection = 'row';
      flexWrap = 'wrap';
    }

    return `<div class="layout-container" style="display: flex; gap: 8px; margin-bottom: 24px; flex-direction: ${flexDirection}; flex-wrap: ${flexWrap}; ${layoutStyle}">${zonesHTML}</div>`;
  };
